import { addDays, DateOnly, format } from '@if/core/date'
import {
  EApplicantType,
  ECreditApplicationStatus,
  EDocumentType,
  EExternalStep,
  EFinancingProgram,
  EOriginSystemId,
  EPaymentMethod,
  ETasksStatus,
  ETaskType,
  EWorksheetDecision,
} from '@if/core/types'
import {
  AccountCircle as AccountCircleIcon,
  AddTask as AddTaskIcon,
  Calculate as CalculateIcon,
  CheckCircle,
  KeyboardDoubleArrowDown as KeyboardDoubleArrowDownIcon,
  Message as MessageIcon,
  Policy as PolicyIcon,
  QuestionAnswer as QuestionAnswerIcon,
} from '@mui/icons-material'
import { Box, Button, Checkbox, Divider, Grid, Paper, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { green } from '@mui/material/colors'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useGetWorksheetRevisions } from '@src/data/api/auto-worksheet/auto-worksheet-api'
import { useGetB2cWorksheetRevisions } from '@src/data/api/b2c-worksheet/b2c-worksheet-api'
import { useCreateBanishment } from '@src/data/api/banisment/banishment'
import {
  useCreateBankAccountRequest,
  useResendUrlForBankAccountRequest,
  useResetPersonalLoanBankAccountRequest,
} from '@src/data/api/bank-account-request/bank-account-request-api'
import { useUnreadEntries } from '@src/data/api/chat-message/chat-message-api'
import { useContinueFinancingDisableReasons } from '@src/data/api/supplemental-loan-api'
import {
  useCreateCvtNumber,
  useDownloadCvtPdf,
} from '@src/data/api/credit-application-contract/credit-application-contract-api'
import {
  useChangeStatus,
  useConfirmAllIncomes,
  useCreateCreditApplicationLock,
  useDeleteCreditApplicationLock,
  useGetCreditApplicationLock,
  useRefinanceCreditApplication,
  useSetPaymentAccount,
  useUpdateCreditApplication,
  useUpdateCreditApplicationLock,
  useUpdateIncomes,
  useUpdateMerchant,
} from '@src/data/api/credit-application/credit-application-api'
import { useCreateComment } from '@src/data/api/credit-task-comment/credit-task-comment-api'
import { useCreateTask, useDeleteTask, useUpdateTask } from '@src/data/api/credit-task/credit-task-api'
import { useResendSignatureLink, useSendSignatureLink } from '@src/data/api/electronic-sign/electronic-sign-api'
import { useInvalidateFullCreditApplication } from '@src/data/api/full-credit-application/full-credit-application-api'
import { useGetIfXpressWorksheetRevisions } from '@src/data/api/ifxpress-worksheet/ifxpress-worksheet-api'
import { useGetMerchantById } from '@src/data/api/merchant/merchant-api'
import { useCreateMessage, useDeleteMessage, useGetMessageList } from '@src/data/api/message/message-api'
import { useGetPersonalLoanWorksheetRevisions } from '@src/data/api/personal-loan-worksheet/personal-loan-worksheet-api'
import { useGetProductsWorksheetRevisions } from '@src/data/api/products-worksheet/products-worksheet-api'
import { useBusinessErrors, useFeatureFlags, useSetBusinessErrors } from '@src/data/selectors/app-config-selectors'
import {
  useAreAllTasksRequiredForPayoutCompleted,
  useCanLockCreditApplication,
  useCurrentCreditApplicationNormsApplicant,
  useCurrentCreditApplicationNormsCoapplicant,
  useCurrentCreditApplicationSpecificNorms,
  useFraudWarningMessages,
  useGetCanConfirmAllIncomes,
  useHasPendingFraudAlert,
  useIncomeConfirmed,
  useGetPreviousApplicantApplications,
  useGetPreviousCoApplicantApplications,
  useGetWorksheetNormWarningMessages,
  useIsFinalDecisionPending,
  useIsPrequalificationPending,
  useIsRefinancing,
  useMostRecentElectronicSignature,
  useProgressionStatus,
  useTaskListToDisplay,
} from '@src/data/selectors/credit-selectors'
import {
  useAreAllDocumentsRequiredForPayOut,
  useAreRequiredDocumentsForCreditOrFinancingSatisfied,
  useHasDocumentsPendingApproval,
} from '@src/data/selectors/document-selectors'
import {
  Applicant,
  ApplicantBankruptcies,
  ApplicantIncomes,
  AutoWorksheet,
  BankAccount,
  Constants,
  CreditApplication,
  CreditApplicationMessage,
  CreditApplicationReservation,
  CreditApplicationReservationDto,
  CreditApplicationTask,
  CreditTaskComment,
  creditTaskSchema,
  Merchant,
  NormsMessage,
  RequiredExternalStep,
  RequiredExternalStepDto,
} from '@src/data/types'
import { usePreferences } from '@src/providers/PreferencesProvider'
import { useCanConsultUnreadMessages, useUser } from '@src/providers/UserProvider'
import { ChangeEvent, useCallback, useEffect, useMemo, useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { BanishmentFormDialog, Breadcrumb, IconButtonWithTooltip, PageError, PageSpinner } from '../../components'
import ConfirmActionDialog from '../../components/ConfirmActionDialog'
import PendingMessage from '../../components/PendingMessage'
import MerchantDialog from '../../components/SelectMerchantDialog'
import { EditBankAccountDto } from '../../data/api/credit-application/EditBankAccountDto'
import { EditIncomesDto } from '../../data/api/credit-application/EditIncomesDto'
import { AutoRslaWorksheet } from '../../data/types/AutoRslaWorksheet'
import { B2cWorksheet } from '../../data/types/B2cWorksheetSchema'
import { BanishmentForm } from '../../data/types/BanSchema'
import { CreditRslaWorksheet } from '../../data/types/CreditRslaWorksheet'
import { EasyCareWorksheet } from '../../data/types/EasyCareWorksheetSchema'
import { IfXpressWorksheet } from '../../data/types/IfXpressWorksheetSchema'
import { PersonalLoanWorksheet } from '../../data/types/PersonalWorksheetSchema'
import { ProductsWorksheet } from '../../data/types/ProductsWorksheetSchema'
import { UpdateCreditApplicationMerchantDto } from '../../data/types/UpdateCreditApplicationMerchantDto'
import { hasFraudAnalysis, InternalMerchants, SUPPORTED_INSTANT_MESSAGE_PROGRAMS } from '../../data/types/constants'
import { reportErrorToConsole } from '../../services/error-logger'
import MechanicalWarrantyWorksheetInformationSection from '../EditWorksheet/EditMechanicalWarrantyWorksheetPage/MechanicalWarranty/components/MechanicalWarrantyWorksheetInformationSection'
import { MechanicalWarrantyWorksheet } from '../EditWorksheet/EditMechanicalWarrantyWorksheetPage/types/mechanicalWarrantyWorksheetSchema'
import AutoRslaWorksheetInformationSection from './components/AutoRslaWorksheetInformation'
import B2cWorksheetInformationSection from './components/B2cWorksheetInformationSection'
import { useAgentWithdrawalReasonDialog } from './agentWithdrawalReason-hooks'
import BanApplicantSelectionDialog from './components/BanApplicantSelectionDialog'
import BankInformationSection from './components/BankInformationSection'
import CreditApplicationLockBanner from './components/CreditApplicationLockBanner'
import CreditApplicationMessageDialog from './components/CreditApplicationMessageDialog'
import CreditApplicationSideBarRail, { SideBarRailItem } from './components/CreditApplicationSideBarRail'
import CreditRslaWorksheetInformationSection from './components/CreditRslaWorksheetInformation'
import EasyCareWorksheetInformationSection from './components/EasyCareWorksheetInformationSection'
import EditBankAcountDialog from './components/EditBankAcountDialog'
import EditBankruptciesDialog from './components/EditBankruptciesDialog'
import EditExpenseDialog from './components/EditExpenseDialog'
import EditIncomesDialog from './components/EditIncomesDialog'
import EditTaskDialog from './components/EditTaskDialog'
import FinancementContractSection from './components/FinancingContractSection'
import IFinanceWorksheetHistoryDialog from './components/IFinanceWorksheetHistoryDialog'
import IfXpressWorksheetInformationSection from './components/IfXpressWorksheetInformation'
import IncomeCalculatorDialog from './components/IncomeCalculator/IncomeCalculatorDialog'
import IncomeSection from './components/IncomeSection'
import MerchantPaymentInstructions from './components/MerchantPaymentInstructions'
import NewTaskDialog from './components/NewTaskCommentDialog'
import ProductsWorksheetInformationSection from './components/ProductsWorksheetInformationSection'
import TaskList from './components/TaskList'
import UnreadMessagesModal from './components/UnreadMessagesModal'
import VouchedSection from './components/VouchedSection'
import ApplicationResume from './components/applicationResume'
import AutoWorksheetHistoryDialog from './components/autoWorksheetHistoryDialog'
import AutoWorksheetInformationSection from './components/autoWorksheetInformationSection'
import CreditDecisionHistoryDialog from './components/creditDecisionHistoryDialog'
import CreditSection from './components/creditSection'
import DocumentList from './components/documentList'
import OtherApplicationList from './components/otherApplicationList'
import PersonalWorksheetInformationSection from './components/personalWorksheetInformationSection'
import PrivateMessage from './components/privateMessage'
import WarningMessageAccordion from './components/warningMessageAccordion'

enum Dialog {
  Job = 'jobDialog',
  Bankruptcies = 'bankruptciesDialog',
  Suppression = 'suppressionDialog',
  Comment = 'commentDialog',
  Task = 'taskDialog',
  Ban = 'banDialog',
  BanApplicantSelection = 'banApplicantSelectionDialog',
  Merch = 'MerchantDialog',
  WorksheetHistory = 'WorksheetHistoryDialog',
  Expenses = 'ExpensesDialog',
  BankAccountInfo = 'BankAccountInfo',
  CreditDecisionHistory = 'creditDecisionHistory',
  Message = 'messageDialog',
  IncomeCalculator = 'incomeCalculatorDialog',
  None = '',
}

interface BusinessValidationErrorResponse {
  response: { data: { message: string; type: 'BusinessValidationError' } }
}

function isBusinessValidationError(error: unknown): error is BusinessValidationErrorResponse {
  if (!error || typeof error !== 'object') return false
  const e = error as Record<string, unknown>
  if (!e.response || typeof e.response !== 'object') return false
  const response = e.response as Record<string, unknown>
  if (!response.data || typeof response.data !== 'object') return false
  const data = response.data as Record<string, unknown>
  return data.type === 'BusinessValidationError' && typeof data.message === 'string' && data.message.length > 0
}

const ViewCreditApplicationPage = (): JSX.Element | null => {
  const { t } = useTranslation()
  const { id, financingProgramId } = useParams<{ id: string; financingProgramId: EFinancingProgram }>()
  const { selectedCompany } = usePreferences()
  const user = useUser()

  const theme = useTheme()
  const error = useBusinessErrors()
  const setBusinessErrors = useSetBusinessErrors()
  const navigate = useNavigate()

  const [updateCreditApplication] = useUpdateCreditApplication()
  const [updateMerchant] = useUpdateMerchant()
  const [confirmAllIncomesHook] = useConfirmAllIncomes()
  const [changeStatusHook] = useChangeStatus()
  const [updateIncomesHook] = useUpdateIncomes()
  const [setPaymentAccountHook] = useSetPaymentAccount()
  const [createCreditApplicationLockHook] = useCreateCreditApplicationLock()
  const [updateCreditApplicationLockHook] = useUpdateCreditApplicationLock()
  const [deleteCreditApplicationLockHook] = useDeleteCreditApplicationLock()
  const [refinanceCreditApplicationHook] = useRefinanceCreditApplication()

  const agentWithdrawalReasonDialog = useAgentWithdrawalReasonDialog()

  const [editingTask, setEditingTask] = useState<CreditApplicationTask>(
    creditTaskSchema.getDefault() as unknown as CreditApplicationTask,
  )
  const [showOnlyTodoTasks, setShowOnlyTodoTasks] = useState<boolean>(true)
  const [editingApplicant, setEditingApplicant] = useState<EApplicantType>(EApplicantType.Applicant)
  const [openDialog, setOpenDialog] = useState<Dialog>(Dialog.None)
  const [showUnreadMessagesModal, setShowUnreadMessagesModal] = useState(false)
  const [createBanishment] = useCreateBanishment()
  const [createTask] = useCreateTask()
  const [updateTask, isUpdatingTask] = useUpdateTask()
  const [deleteTask] = useDeleteTask()
  const [createComment] = useCreateComment()
  const [createCvtNumber] = useCreateCvtNumber()
  const [downloadCvtPdf] = useDownloadCvtPdf()
  const [createBankAccountRequest] = useCreateBankAccountRequest()
  const [resendBankAccountRequestUrl] = useResendUrlForBankAccountRequest()
  const [resetBankAccountRequest] = useResetPersonalLoanBankAccountRequest()
  const [createMessage] = useCreateMessage()
  const [deleteMessage] = useDeleteMessage()
  const invalidateFullCreditApplication = useInvalidateFullCreditApplication()
  const { creditApplication, isLoading: isLoadingCreditApplication } = useCreditApplicationData()

  const [lock, isLoadingLock] = useGetCreditApplicationLock(financingProgramId, id)

  const [autoWorksheetRevisions, isLoadingAutoRevisions] = useGetWorksheetRevisions(
    financingProgramId === EFinancingProgram.Auto ? id : undefined,
  )
  const [personalLoanWorksheetRevisions, isLoadingPersonalLoanRevisions] = useGetPersonalLoanWorksheetRevisions(
    financingProgramId === EFinancingProgram.Personal ? id : undefined,
  )
  const [b2cWorksheetRevisions, isLoadingB2cRevisions] = useGetB2cWorksheetRevisions(
    financingProgramId === EFinancingProgram.B2c ? id : undefined,
  )
  const [productsWorksheetRevisions, isLoadingProductsRevisions] = useGetProductsWorksheetRevisions(
    financingProgramId === EFinancingProgram.Products ? id : undefined,
  )
  const [ifXpressWorksheetRevisions, isLoadingIfXpressRevisions] = useGetIfXpressWorksheetRevisions(
    financingProgramId === EFinancingProgram.IfXpress ? id : undefined,
  )

  const isLoading = isLoadingCreditApplication || isLoadingLock
  const isLoadingPreviousApplicantApplication = false
  const isLoadingPreviousCoApplicantApplication = false
  const [merchant] = useGetMerchantById(creditApplication?.merchantId ?? '')
  const isPrequalificationPending = useIsPrequalificationPending()

  const { documents, requiredDocuments } = useCreditApplicationData()
  const hasDocumentsPendingApproval = useHasDocumentsPendingApproval(documents)
  const [continueFinancingDisableReasonList] = useContinueFinancingDisableReasons(financingProgramId, id)
  const creditTasks = useTaskListToDisplay()
  const areAllTasksRequiredForPayoutCompleted = useAreAllTasksRequiredForPayoutCompleted()
  const areAllDocumentsRequiredForPayOut = useAreAllDocumentsRequiredForPayOut(documents, requiredDocuments)

  const [messages] = useGetMessageList(id, financingProgramId)
  const creditApplicationMessage = messages ?? []
  const privateMessages = messages?.filter((m) => m.isPrivate) ?? []
  const canConfirmIncomeFromRedux = useGetCanConfirmAllIncomes(true)
  const hasPendingFraudAlert = useHasPendingFraudAlert()
  const incomeConfirmed = useIncomeConfirmed()

  const { worksheet, requiredExternalSteps } = useCreditApplicationData()
  const areRequiredDocsForCreditOrFinancingSatisfied = useAreRequiredDocumentsForCreditOrFinancingSatisfied(
    documents,
    requiredDocuments,
  )
  const canConfirmIncome = canConfirmIncomeFromRedux && areRequiredDocsForCreditOrFinancingSatisfied
  const autoWorksheet = worksheet as AutoWorksheet | undefined
  const personalLoanWorksheet = worksheet as PersonalLoanWorksheet | undefined
  const b2cWorksheet = worksheet as B2cWorksheet | undefined
  const autoRslaWorksheet = worksheet as AutoRslaWorksheet | undefined
  const creditRslaWorksheet = worksheet as CreditRslaWorksheet | undefined
  const productsWorksheet = worksheet as ProductsWorksheet | undefined
  const ifXpressWorksheet = worksheet as IfXpressWorksheet | undefined
  const mechanicalWarrantyWorksheet = worksheet as MechanicalWarrantyWorksheet | undefined
  const easyCareWorksheet = worksheet as EasyCareWorksheet | undefined
  const mostRecentElectronicSignature = useMostRecentElectronicSignature()
  const electronicSignatureStatus = mostRecentElectronicSignature?.currentStep ?? null
  const applicantNormsMessage: NormsMessage[] = useCurrentCreditApplicationNormsApplicant()
  const coapplicantNormsMessage: NormsMessage[] = useCurrentCreditApplicationNormsCoapplicant()
  const commonNormsMessage: NormsMessage[] = useCurrentCreditApplicationSpecificNorms()
  const applicantPreviousApplications = useGetPreviousApplicantApplications()
  const coApplicantPreviousApplications = useGetPreviousCoApplicantApplications()
  const worksheetNormsMessages: NormsMessage[] = useGetWorksheetNormWarningMessages()
  const canLockCreditApplication = useCanLockCreditApplication()
  const applicationIsDraft = creditApplication?.status === ECreditApplicationStatus.Draft
  const applicationStatus = useProgressionStatus()
  const finalDecisionIsPending = useIsFinalDecisionPending()
  const worksheetIsApproved = personalLoanWorksheet?.finalDecision.decision === EWorksheetDecision.Approved
  const isRefinancing = useIsRefinancing()
  const editDisabled = creditApplication?.editLocked || finalDecisionIsPending
  const showPreviousApplication =
    !(isLoadingPreviousApplicantApplication || isLoadingPreviousCoApplicantApplication) &&
    (applicantPreviousApplications.length > 0 || coApplicantPreviousApplications.length > 0)
  const featureFlags = useFeatureFlags()
  const canConsultUnreadMessages = useCanConsultUnreadMessages()

  const [unreadEntries] = useUnreadEntries(featureFlags.useInstantMessage && canConsultUnreadMessages)

  const showFraudAnalysis = featureFlags.useFraudFlags && hasFraudAnalysis(creditApplication?.financingProgramId)
  const applicantFraudWarnings: NormsMessage[] = useFraudWarningMessages(EApplicantType.Applicant, showFraudAnalysis)
  const coapplicantFraudWarnings: NormsMessage[] = useFraudWarningMessages(
    EApplicantType.CoApplicant,
    showFraudAnalysis,
  )

  const financingProgramConfig = creditApplication
    ? Constants.financingProgramConfigs[creditApplication.financingProgramId]
    : null

  const sideBarRailItems = useMemo<SideBarRailItem[]>(() => {
    if (!creditApplication) return []
    const items: SideBarRailItem[] = []

    if (financingProgramConfig?.usePrivateMessage)
      items.push({
        key: 'privateMessage',
        tooltip: t('privateMessage.addMessage'),
        icon: <QuestionAnswerIcon color="primary" />,
        onClick: () => setOpenDialog(Dialog.Message),
      })

    if (showFraudAnalysis)
      items.push({
        key: 'fraud',
        tooltip: t('fraudFlags.title'),
        icon: <PolicyIcon />,
        href: '../fraud-flags',
        target: '_blank',
      })

    if (
      featureFlags.useInstantMessage &&
      SUPPORTED_INSTANT_MESSAGE_PROGRAMS.includes(creditApplication.financingProgramId) &&
      !InternalMerchants.includes(creditApplication.merchantId) &&
      creditApplication.originSystemId !== EOriginSystemId.Autologiq
    )
      items.push({
        key: 'chat',
        tooltip: t('instantMessage.title'),
        icon: <MessageIcon />,
        href: `/chat-dashboard/${financingProgramId}/${creditApplication.id}`,
        target: '_blank',
      })

    items.push({
      key: 'calculator',
      tooltip: t('menus.financing.calculator'),
      icon: <CalculateIcon />,
      href: '../calculator',
      target: '_blank',
    })

    return items
  }, [creditApplication, financingProgramConfig, featureFlags, showFraudAnalysis, financingProgramId, t])

  const docs = documents
  const [sendSignatureLink] = useSendSignatureLink()
  const [resendSignatureLink, , ,] = useResendSignatureLink()

  const creditFilteredTasks = useMemo(() => {
    if (showOnlyTodoTasks) {
      return creditTasks.filter((x) => x.status === ETasksStatus.ToDo)
    }
    return creditTasks
  }, [creditTasks, showOnlyTodoTasks])

  const incomeTasks = useMemo(() => {
    return creditTasks.filter((x) => x.typeId === ETaskType.ConfirmJobIncome)
  }, [creditTasks])

  const otherIncomeTasks = useMemo(() => {
    return creditTasks.filter((x) => x.typeId === ETaskType.ConfirmOtherIncome)
  }, [creditTasks])

  const hasConfirmedHighEstimateFunding = useMemo(() => {
    return (
      creditTasks.find(
        (task) => task.typeId === ETaskType.ConfirmHighEstimateFunding && task.status === ETasksStatus.Completed,
      ) !== undefined
    )
  }, [creditTasks])

  const [disabledDialogButton, setDisabledDialogButton] = useState(false)


  const closeDialog = useCallback(() => {
    setOpenDialog(Dialog.None)
    setDisabledDialogButton(false)
  }, [setOpenDialog])

  const breadCrumbs = [
    { path: '/', label: t('breadcrumbs.home') },
    { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
    { path: '#', label: t('breadcrumbs.application').concat(` #${creditApplication?.referenceNumber}`) },
  ]

  const onMerchantChanged = useCallback(
    async (newMerchant: Merchant) => {
      const updatedMerchant = {
        versionTag: creditApplication?.versionTag,
        merchantId: newMerchant.id,
        creditApplicationId: creditApplication?.id,
        financingProgramId: creditApplication?.financingProgramId,
      } as UpdateCreditApplicationMerchantDto
      setDisabledDialogButton(true)
      await updateMerchant(updatedMerchant)
      if (personalLoanWorksheet?.id && creditApplication?.id) {
        await invalidateFullCreditApplication(creditApplication.id, creditApplication.financingProgramId)
      }
      closeDialog()
    },
    [creditApplication, updateMerchant, closeDialog, personalLoanWorksheet, invalidateFullCreditApplication],
  )
  const handleEditTaskDialog = useCallback((task: CreditApplicationTask) => {
    if (task.id) {
      setEditingTask(task)
    } else {
      setEditingTask(creditTaskSchema.getDefault() as unknown as CreditApplicationTask)
    }
    setOpenDialog(Dialog.Task)
  }, [])

  const handleBanApplicant = useCallback(() => {
    if (creditApplication?.coApplicant) {
      setOpenDialog(Dialog.BanApplicantSelection)
    } else {
      setEditingApplicant(EApplicantType.Applicant)
      setOpenDialog(Dialog.Ban)
    }
  }, [creditApplication?.coApplicant])

  const handleBanApplicantSelected = useCallback((applicantType: EApplicantType) => {
    setEditingApplicant(applicantType)
    setOpenDialog(Dialog.Ban)
  }, [])

  const handleSaveTask = useCallback(
    async (task: CreditApplicationTask) => {
      task.financingProgramId = financingProgramId
      setDisabledDialogButton(true)
      try {
        if (task.id) {
          await updateTask(task)
        } else {
          task.creditApplicationId = id
          await createTask(task)
        }
        closeDialog()
      } catch (error: unknown) {
        if (isBusinessValidationError(error)) {
          closeDialog()
          setBusinessErrors([{ message: error.response.data.message }])
          window.scrollTo({ top: 0, behavior: 'smooth' })
        } else {
          reportErrorToConsole('Error saving task')
        }
      } finally {
        setDisabledDialogButton(false)
      }
    },
    [id, closeDialog, financingProgramId, updateTask, createTask, setBusinessErrors],
  )

  function findRequiredStepsByApplicantTypeAndExternalStepId(data: EApplicantType, externalStepId: EExternalStep) {
    return requiredExternalSteps.find((element) => {
      return element.applicantType === data && element.externalStepId === externalStepId
    })
  }

  const handleResetdBankAccountRequest = useCallback(
    async (data: 'applicant' | 'coApplicant') => {
      if (creditApplication?.id) {
        const bankAccountRequestDto: RequiredExternalStepDto = {
          applicantType: data,
          creditApplicationId: creditApplication.id,
          financingProgramId: creditApplication.financingProgramId,
          contactClient: false,
        }
        try {
          // Mutation invalidates fullCreditApplication cache which syncs to Redux
          await resetBankAccountRequest(bankAccountRequestDto)
        } catch {
          reportErrorToConsole('Error resetting bank account request')
        }
      }
    },
    [creditApplication, resetBankAccountRequest],
  )

  const handleSaveBankAccountRequest = async (data: EApplicantType) => {
    const isFound = requiredExternalSteps.find(
      (element: RequiredExternalStep) =>
        element.applicantType === data && element.externalStepId === EExternalStep.BankAccount,
    )
    if (creditApplication?.id) {
      const bankAccountRequestDto: RequiredExternalStepDto = {
        applicantType: data,
        creditApplicationId: creditApplication.id,
        financingProgramId: creditApplication.financingProgramId,
        contactClient: true,
      }
      if (isFound) {
        try {
          await resendBankAccountRequestUrl(bankAccountRequestDto)
        } catch {
          reportErrorToConsole('Error resending bank account request URL')
        }
      } else {
        try {
          await createBankAccountRequest(bankAccountRequestDto)
        } catch {
          reportErrorToConsole('Error creating bank account request')
        }
      }
    }
  }

  const handleNewComment = useCallback((task: CreditApplicationTask) => {
    setEditingTask(task)
    setOpenDialog(Dialog.Comment)
  }, [])

  const handleShowOnlyTodoTasks = (event: ChangeEvent<HTMLInputElement>) => {
    setShowOnlyTodoTasks(event.target.checked)
  }

  const handleSaveTaskComment = async (comment: CreditTaskComment) => {
    comment.taskId = editingTask.id
    comment.financingProgramId = financingProgramId
    comment.creditApplicationId = editingTask.creditApplicationId ?? null
    setDisabledDialogButton(true)
    try {
      await createComment(comment)
      closeDialog()
    } catch {
      reportErrorToConsole('Error creating comment')
    } finally {
      setDisabledDialogButton(false)
    }
  }

  const handleStatusChange = useCallback(
    async (status: ETasksStatus, task: CreditApplicationTask) => {
      const upTask = { ...task }
      upTask.status = status
      upTask.financingProgramId = financingProgramId
      try {
        await updateTask(upTask)
      } catch (error: unknown) {
        if (isBusinessValidationError(error)) {
          setBusinessErrors([{ message: error.response.data.message }])
          window.scrollTo({ top: 0, behavior: 'smooth' })
        } else {
          reportErrorToConsole('Error updating task status')
        }
      }
    },
    [financingProgramId, updateTask, setBusinessErrors],
  )

  const handleTaskDeletionClicked = (task: CreditApplicationTask) => {
    setEditingTask(task)
    setOpenDialog(Dialog.Suppression)
  }

  const handleTaskDeletionConfirmed = async () => {
    try {
      await deleteTask(editingTask)
      closeDialog()
    } catch {
      reportErrorToConsole('Error deleting task')
    }
  }

  const handleBanConfirmed = async (formData: BanishmentForm) => {
    if (creditApplication) {
      const banishment = {
        reason: formData.reason,
        expirationDate: formData.expirationDate,
        firstName: formData.firstName,
        lastName: formData.lastName,
        birthDate: formData.birthDate,
        sin: formData.sin,
      }

      try {
        await createBanishment(banishment)
        closeDialog()
      } catch (error) {
        reportErrorToConsole(error)
      }
    }
  }

  const handleJobUpdateClicked = (data: EApplicantType) => {
    setEditingApplicant(data)
    setOpenDialog(Dialog.Job)
  }

  const handleOpenCalculatorDialog = (applicantType: EApplicantType) => {
    setEditingApplicant(applicantType)
    setOpenDialog(Dialog.IncomeCalculator)
  }


  const handleBankAccountClicked = useCallback(() => {
    setOpenDialog(Dialog.BankAccountInfo)
  }, [])

  const handleExpenseClicked = (data: EApplicantType) => {
    setEditingApplicant(data)
    setOpenDialog(Dialog.Expenses)
  }
  const handleEditBankruptciesClicked = (data: EApplicantType) => {
    setEditingApplicant(data)
    setOpenDialog(Dialog.Bankruptcies)
  }

  const handleConfirmAllIncomes = async () => {
    try {
      await confirmAllIncomesHook({ id: id!, financingProgramId: financingProgramId! })
      await invalidateFullCreditApplication(id!, financingProgramId!)
    } catch (error) {
      reportErrorToConsole(error)
    }
  }

  const handleApplicantUpdate = useCallback(
    async (applicantToUpdate: ApplicantBankruptcies) => {
      const updatingCreditApplication: Partial<CreditApplication> =
        editingApplicant === EApplicantType.Applicant
          ? ({
              ...creditApplication,
              applicant: {
                ...creditApplication?.applicant,
                ...applicantToUpdate,
                expenses: { ...creditApplication?.applicant?.expenses, isLaw134Confirmed: false },
              },
            } as Partial<CreditApplication>)
          : ({
              ...creditApplication,
              coApplicant: {
                ...creditApplication?.coApplicant,
                ...applicantToUpdate,
                expenses: { ...creditApplication?.coApplicant?.expenses, isLaw134Confirmed: false },
              },
            } as Partial<CreditApplication>)
      setDisabledDialogButton(true)
      await updateCreditApplication(updatingCreditApplication)
      closeDialog()
    },
    [creditApplication, editingApplicant, updateCreditApplication, closeDialog],
  )

  const handleExpenseUpdate = useCallback(
    async (applicantToUpdate: Applicant) => {
      const updatingCreditApplication: Partial<CreditApplication> =
        editingApplicant === EApplicantType.Applicant
          ? ({
              ...creditApplication,
              applicant: { ...creditApplication?.applicant, ...applicantToUpdate },
            } as Partial<CreditApplication>)
          : ({
              ...creditApplication,
              coApplicant: { ...creditApplication?.coApplicant, ...applicantToUpdate },
            } as Partial<CreditApplication>)
      setDisabledDialogButton(true)
      await updateCreditApplication(updatingCreditApplication)
      closeDialog()
    },
    [creditApplication, editingApplicant, updateCreditApplication, closeDialog],
  )

  const handleLockingCreditApplication = useCallback(
    (minutes: number) => {
      if (creditApplication?.id) {
        const entityLock: CreditApplicationReservationDto = {
          financingProgramId: creditApplication.financingProgramId,
          creditApplicationId: creditApplication.id,
          durationInMinutes: minutes,
        }

        createCreditApplicationLockHook(entityLock).catch(reportErrorToConsole)
      }
    },
    [creditApplication, createCreditApplicationLockHook],
  )

  const handleAddingPrivateMessageToCreditApplication = useCallback(
    (message: string) => {
      if (creditApplication?.id) {
        const messageToAdd = {
          creditApplicationId: creditApplication.id,
          financingProgramId: creditApplication.financingProgramId,
          content: message,
          isPrivate: true,
        }
        createMessage(messageToAdd)
          .then(() => {
            closeDialog()
          })
          .catch(reportErrorToConsole)
      }
    },
    [creditApplication, createMessage, closeDialog],
  )
  const handleDeletingPrivateMessage = (message: CreditApplicationMessage) => {
    if (creditApplication?.id) {
      deleteMessage({
        id: message.id,
        creditApplicationId: creditApplication.id,
        financingProgramId: creditApplication.financingProgramId,
      }).catch(reportErrorToConsole)
    }
  }

  const handleAddingTimeToLock = (lockWithChange: CreditApplicationReservation, minutes: number) => {
    if (minutes > 0) {
      const lockToUpdate = { ...lockWithChange }
      const date = new Date(lockToUpdate.expiresOn)
      date.setMinutes(date.getMinutes() + minutes)
      lockToUpdate.expiresOn = date
      updateCreditApplicationLockHook(lockToUpdate).catch(reportErrorToConsole)
    }
  }

  const handleChangeCreditApplicationStatus = () => {
    if (creditApplication) {
      const currentStatus = creditApplication.status
      if (currentStatus === ECreditApplicationStatus.Completed) return
      if (currentStatus !== ECreditApplicationStatus.Cancelled)
        changeStatusHook({
          financingProgramId: creditApplication.financingProgramId,
          creditApplicationId: creditApplication.id,
          status: ECreditApplicationStatus.Cancelled,
          versionTag: creditApplication.versionTag!,
        })
          .then(() => {
            if (featureFlags.useAgentWithdrawalReason)
              void agentWithdrawalReasonDialog.open({
                financingProgramId: creditApplication.financingProgramId,
                creditApplicationId: creditApplication.id,
              })
          })
          .catch(reportErrorToConsole)
      else
        changeStatusHook({
          financingProgramId: creditApplication.financingProgramId,
          creditApplicationId: creditApplication.id,
          status: ECreditApplicationStatus.Active,
          versionTag: creditApplication.versionTag!,
        }).catch(reportErrorToConsole)
    }
  }

  const handleUpdateIncomes = async (applicantToUpdate: ApplicantIncomes) => {
    if (creditApplication?.id) {
      const incomesToUpdate: EditIncomesDto = {
        incomes: applicantToUpdate.currentJobs,
        otherIncomes: applicantToUpdate.otherIncomes,
        creditApplicationId: creditApplication.id,
        applicantType: editingApplicant,
        financingProgramId: creditApplication.financingProgramId,
        versionTag: creditApplication.versionTag!,
      }
      setDisabledDialogButton(true)
      try {
        await updateIncomesHook(incomesToUpdate)
        closeDialog()
      } finally {
        setDisabledDialogButton(false)
      }
    }
  }

  const handleSetPaymentAccount = useCallback(
    async (data: BankAccount) => {
      if (creditApplication) {
        const bankAccountToUpdate: EditBankAccountDto = {
          creditApplicationId: creditApplication.id,
          bankName: data.bankName,
          transitNumber: data.transitNumber,
          institutionNumber: data.institutionNumber,
          accountNumber: data.accountNumber,
          financingProgramId: creditApplication.financingProgramId,
        }

        setDisabledDialogButton(true)
        await setPaymentAccountHook(bankAccountToUpdate)
      }
      closeDialog()
    },
    [creditApplication, setPaymentAccountHook, closeDialog],
  )

  const handleGenerateCvtNumber = useCallback(async () => {
    if (id && financingProgramId) {
      try {
        await createCvtNumber({ creditApplicationId: id, financingProgramId })
      } catch (error) {
        reportErrorToConsole(error)
      }
    }
  }, [id, financingProgramId, createCvtNumber])

  const handlePrintCvt = useCallback(async () => {
    if (id && financingProgramId) {
      try {
        await downloadCvtPdf({ creditApplicationId: id, financingProgramId })
      } catch (error) {
        reportErrorToConsole(error)
      }
    }
  }, [financingProgramId, id, downloadCvtPdf])

  const [disableResendLinkButton, setDisableResendLinkButton] = useState(false)

  const handleResendElectronicSign = useCallback(() => {
    if (financingProgramId && id) {
      setDisableResendLinkButton(true)
      resendSignatureLink({ financingProgramId, creditApplicationId: id }).catch(() =>
        setDisableResendLinkButton(false),
      )
    }
  }, [financingProgramId, id, resendSignatureLink])

  const handleSendElectronicSign = useCallback(async () => {
    if (!financingProgramId || !id) return
    await sendSignatureLink({ financingProgramId, creditApplicationId: id })
      .then(() => setDisableResendLinkButton(false))
      .catch(reportErrorToConsole)
  }, [financingProgramId, id, sendSignatureLink])

  const handleRefinanceCreditApplication = useCallback(async () => {
    if (id && financingProgramId) {
      try {
        const data = await refinanceCreditApplicationHook({ creditApplicationId: id, financingProgramId })
        navigate(`/Applications/${data.financingProgramId}/${data.id}/view`, { replace: true })
      } catch (error: unknown) {
        if (isBusinessValidationError(error)) {
          setBusinessErrors([{ message: error.response.data.message }])
          window.scrollTo({ top: 0, behavior: 'smooth' })
        } else {
          reportErrorToConsole(error)
        }
      }
    }
  }, [financingProgramId, id, refinanceCreditApplicationHook, navigate, setBusinessErrors])

  const renderDialog = (param: Dialog, creditApplication: CreditApplication) => {
    switch (param) {
      case Dialog.Job: {
        const applicantJobs =
          editingApplicant === EApplicantType.Applicant
            ? creditApplication.applicant.currentJobs ?? []
            : creditApplication.coApplicant?.currentJobs ?? []
        const otherIncomes =
          editingApplicant === EApplicantType.Applicant
            ? creditApplication.applicant.otherIncomes ?? []
            : creditApplication.coApplicant?.otherIncomes ?? []
        return (
          <EditIncomesDialog
            open
            title={
              editingApplicant === EApplicantType.Applicant
                ? t('editCreditApplication.income.job.updateApplicantIncome')
                : t('editCreditApplication.income.job.updateCoapplicantIncome')
            }
            defaultValue={
              editingApplicant === EApplicantType.Applicant || creditApplication.coApplicant
                ? { currentJobs: applicantJobs, otherIncomes, financingProgramId: creditApplication.financingProgramId }
                : undefined
            }
            onConfirm={(data) => {
              void handleUpdateIncomes(data)
            }}
            onCancel={() => {
              closeDialog()
            }}
            disableConfirm={disabledDialogButton || isRefinancing}
          />
        )
      }
      case Dialog.Bankruptcies:
        return (
          <EditBankruptciesDialog
            open
            creditApplication={creditApplication}
            applicant={
              editingApplicant === EApplicantType.Applicant
                ? creditApplication.applicant
                : (creditApplication.coApplicant ?? undefined)
            }
            onConfirm={handleApplicantUpdate}
            onCancel={closeDialog}
            disableConfirm={disabledDialogButton}
          />
        )
      case Dialog.Merch:
        return (
          <MerchantDialog
            open
            title={t('worksheet.modifyMerchant')}
            label={t('taskManager.merchantName')}
            onConfirm={onMerchantChanged}
            onCancel={closeDialog}
            allowMultiple={false}
            financingProgramId={creditApplication.financingProgramId}
            disableConfirm={disabledDialogButton || isRefinancing}
            financingCompanyId={selectedCompany}
          />
        )
      case Dialog.Task:
        return (
          <EditTaskDialog
            open
            title={t(editingTask.typeId ? 'tasks.editTask' : 'tasks.newTask')}
            defaultValues={editingTask}
            onConfirm={handleSaveTask}
            onCancel={closeDialog}
            disableConfirm={disabledDialogButton}
          />
        )
      case Dialog.Comment:
        return (
          <NewTaskDialog
            open
            title={t('tasks.newComment')}
            disableConfirm={disabledDialogButton}
            onConfirm={handleSaveTaskComment}
            onCancel={closeDialog}
          />
        )

      case Dialog.Suppression:
        return (
          <ConfirmActionDialog
            open
            title={t('tasks.suppression')}
            onConfirm={handleTaskDeletionConfirmed}
            onCancel={closeDialog}
            text={t('tasks.messageSuppression')}
          />
        )
      case Dialog.Ban: {
        const source =
          editingApplicant === EApplicantType.CoApplicant ? creditApplication.coApplicant : creditApplication.applicant
        const defaultValues = source
          ? {
              firstName: source.firstName,
              lastName: source.lastName,
              birthDate: new DateOnly(source.birthDate),
              sin: source.sin || '',
              description: '',
              expirationDate: null,
            }
          : {}

        return (
          <BanishmentFormDialog
            open
            onConfirm={handleBanConfirmed}
            onCancel={closeDialog}
            defaultValues={defaultValues}
            readonly={{
              firstName: !!source,
              lastName: !!source,
              birthDate: !!source,
              sin: !!source,
            }}
          />
        )
      }
      case Dialog.BanApplicantSelection:
        return (
          <BanApplicantSelectionDialog
            open
            onCancel={closeDialog}
            onSelectApplicant={handleBanApplicantSelected}
            hasCoApplicant={Boolean(creditApplication.coApplicant)}
            creditApplication={creditApplication}
          />
        )
      case Dialog.WorksheetHistory:
        if (creditApplication.financingProgramId === EFinancingProgram.Auto) {
          return (
            <AutoWorksheetHistoryDialog
              open={openDialog === Dialog.WorksheetHistory}
              worksheetRevisions={autoWorksheetRevisions ?? []}
              creditApplicationId={id}
              onClose={closeDialog}
              isLoading={isLoadingAutoRevisions}
            />
          )
        }
        if (creditApplication.financingProgramId === EFinancingProgram.Personal) {
          return (
            <IFinanceWorksheetHistoryDialog
              open={openDialog === Dialog.WorksheetHistory}
              creditApplicationId={id}
              onClose={closeDialog}
              worksheetRevisions={personalLoanWorksheetRevisions ?? []}
              isLoading={isLoadingPersonalLoanRevisions}
            />
          )
        }
        if (creditApplication.financingProgramId === EFinancingProgram.B2c) {
          return (
            <IFinanceWorksheetHistoryDialog
              open={openDialog === Dialog.WorksheetHistory}
              creditApplicationId={id}
              onClose={closeDialog}
              worksheetRevisions={b2cWorksheetRevisions ?? []}
              isLoading={isLoadingB2cRevisions}
            />
          )
        }
        if (creditApplication.financingProgramId === EFinancingProgram.Products) {
          return (
            <IFinanceWorksheetHistoryDialog
              open={openDialog === Dialog.WorksheetHistory}
              creditApplicationId={id}
              onClose={closeDialog}
              worksheetRevisions={productsWorksheetRevisions ?? []}
              isLoading={isLoadingProductsRevisions}
            />
          )
        }
        if (creditApplication.financingProgramId === EFinancingProgram.IfXpress) {
          return (
            <IFinanceWorksheetHistoryDialog
              open={openDialog === Dialog.WorksheetHistory}
              creditApplicationId={id}
              onClose={closeDialog}
              worksheetRevisions={ifXpressWorksheetRevisions ?? []}
              isLoading={isLoadingIfXpressRevisions}
            />
          )
        }
        throw new Error('unexpected financing program')

        break
      case Dialog.Expenses:
        return (
          <EditExpenseDialog
            open={openDialog === Dialog.Expenses}
            title={
              editingApplicant === EApplicantType.Applicant
                ? t('financeContract.updateApplicant')
                : t('financeContract.updateCoApplicant')
            }
            applicant={
              editingApplicant === EApplicantType.Applicant
                ? creditApplication.applicant
                : (creditApplication.coApplicant ?? undefined)
            }
            onConfirm={handleExpenseUpdate}
            onCancel={closeDialog}
            financingProgramId={creditApplication.financingProgramId}
          />
        )
        break
      case Dialog.BankAccountInfo:
        return (
          <EditBankAcountDialog
            open={openDialog === Dialog.BankAccountInfo}
            bankAccount={creditApplication.paymentBankAccount}
            onConfirm={handleSetPaymentAccount}
            onCancel={closeDialog}
          />
        )
        break
      case Dialog.CreditDecisionHistory:
        return (
          <CreditDecisionHistoryDialog
            open={openDialog === Dialog.CreditDecisionHistory}
            creditApplicationId={id!}
            onClose={closeDialog}
            financingProgramId={financingProgramId!}
          />
        )
      case Dialog.Message:
        if (!financingProgramConfig?.usePrivateMessage) return null
        return (
          <CreditApplicationMessageDialog
            open={openDialog === Dialog.Message}
            onClose={closeDialog}
            app={creditApplication}
            handleAddingMessageToCreditApplication={handleAddingPrivateMessageToCreditApplication}
          />
        )
      case Dialog.IncomeCalculator: {
        const calculatorApplicant =
          editingApplicant === EApplicantType.CoApplicant ? creditApplication.coApplicant : creditApplication.applicant
        return (
          <IncomeCalculatorDialog
            open
            onClose={closeDialog}
            provinceIso={calculatorApplicant?.currentAddress.stateIso ?? null}
          />
        )
      }
      default:
        return null
    }
  }
  useEffect(() => {
    if (!personalLoanWorksheet) return

    const maxDateToSendInvoice = format(addDays(new Date(personalLoanWorksheet.firstPaymentOn), -5), 'yyyy-MM-dd')
    const today = format(new Date(), 'yyyy-MM-dd')

    const hasClinicInvoice = docs.some((doc) => doc.typeId === EDocumentType.ClinicInvoice)

    if (!hasClinicInvoice && personalLoanWorksheet.isHighEstimate && today > maxDateToSendInvoice) {
      setBusinessErrors([{ message: 'common.errors.highEstimateUploadInvoiceDelayPassed' }])
    }
  }, [docs, personalLoanWorksheet, setBusinessErrors])

  // Show modal when unread messages exist on page load
  useEffect(() => {
    if (!id) return
    const hasUnread = unreadEntries.some((e) => e.creditApplicationId === id)
    if (hasUnread && !isLoadingCreditApplication && canConsultUnreadMessages) {
      setShowUnreadMessagesModal(true)
    }
  }, [unreadEntries, id, isLoadingCreditApplication, canConsultUnreadMessages])

  return (
    <>
      <PageSpinner isLoading={isLoading} />
      {creditApplication && (
        <UnreadMessagesModal
          open={showUnreadMessagesModal}
          creditApp={creditApplication}
          financingProgramId={financingProgramId!}
          onClose={() => setShowUnreadMessagesModal(false)}
        />
      )}
      {!isLoading && creditApplication && merchant && (
        <>
          <CreditApplicationSideBarRail items={sideBarRailItems} />

          <div style={{ marginBottom: 25 }}>
            {user?.rights.canEditCreditApp && (
              <CreditApplicationLockBanner
                lock={lock}
                currentUser={user}
                handleLockCreditApplication={handleLockingCreditApplication}
                handleAddTimeToLock={handleAddingTimeToLock}
                handleDeleteLock={(creditLock: CreditApplicationReservation) => deleteCreditApplicationLockHook(creditLock)}
              />
            )}

            <Breadcrumb trees={breadCrumbs} />

            <PageError errors={error} />
            {(finalDecisionIsPending || isPrequalificationPending) && <PendingMessage />}
            <ApplicationResume
              creditApplication={creditApplication}
              creditApplicationMessage={creditApplicationMessage}
              merchant={merchant}
              onStatusChange={handleChangeCreditApplicationStatus}
              onMerchantChanged={() => setOpenDialog(Dialog.Merch)}
              creditApplicationCanBeLocked={canLockCreditApplication}
              loanId={creditApplication.loanId}
              user={user}
              applicationStatus={applicationStatus!}
              editDisabled={editDisabled}
              finalDecisionIsPending={finalDecisionIsPending}
              allTasksRequiredForPayoutCompleted={areAllTasksRequiredForPayoutCompleted}
              allDocumentsRequiredForPayOutCompleted={areAllDocumentsRequiredForPayOut}
              onBanApplicant={handleBanApplicant}
              onAgentWithdrawalReason={() =>
                void agentWithdrawalReasonDialog.open({
                  financingProgramId: creditApplication.financingProgramId,
                  creditApplicationId: creditApplication.id,
                })
              }
              hasDocumentsPendingApproval={hasDocumentsPendingApproval}
              ishighEstimate={personalLoanWorksheet?.isHighEstimate === true}
              hasConfirmedHighEstimateFunding={hasConfirmedHighEstimateFunding}
              incomeConfirmed={incomeConfirmed}
            />

            {financingProgramId === EFinancingProgram.Personal && <MerchantPaymentInstructions merchant={merchant} />}

            {showPreviousApplication && (
              <OtherApplicationList
                applicantPreviousApplications={applicantPreviousApplications}
                coApplicantPreviousApplications={coApplicantPreviousApplications}
                hasCoapplicant={creditApplication.coApplicant !== null}
              />
            )}
            <PrivateMessage
              messages={privateMessages}
              currentUser={user}
              handleDeletingPrivateMessage={handleDeletingPrivateMessage}
              creditAppCreationDate={creditApplication.createdOn}
            />
            {(applicantNormsMessage.length > 0 ||
              coapplicantNormsMessage.length > 0 ||
              commonNormsMessage.length > 0 ||
              worksheetNormsMessages.length > 0 ||
              (showFraudAnalysis && (applicantFraudWarnings.length > 0 || coapplicantFraudWarnings.length > 0))) && (
              <WarningMessageAccordion
                applicantCreditWarnings={applicantNormsMessage}
                coapplicantCreditWarnings={coapplicantNormsMessage}
                commonCreditWarnings={commonNormsMessage}
                worksheetWarnings={worksheetNormsMessages}
                applicantFraudWarnings={applicantFraudWarnings}
                coapplicantFraudWarnings={coapplicantFraudWarnings}
                expanded
              />
            )}

            {/** Task List */}
            <Paper elevation={3} sx={{ borderRadius: 2, p: 2, mt: 3, mb: 3 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography
                    variant="body1"
                    component="div"
                    sx={{
                      float: 'left',
                      border: '1px solid',
                      borderRadius: 2,
                      borderColor: theme.palette.divider,
                      p: 1,
                    }}
                  >
                    {t('tasks.tasks')}
                  </Typography>

                  {user?.rights.canCreateTasks &&
                    (!editDisabled || (featureFlags.useTaskFollowUpOnPPBKApp && creditApplication.loanId)) && (
                      <IconButtonWithTooltip
                        tooltip={t('tasks.newTask')}
                        icon={<AddTaskIcon color="primary" />}
                        onClick={() =>
                          handleEditTaskDialog(creditTaskSchema.getDefault() as unknown as CreditApplicationTask)
                        }
                      />
                    )}
                  <AccountCircleIcon />
                </Stack>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body1" component="div">
                    {t('tasks.toDo')}
                  </Typography>
                  <Checkbox onChange={handleShowOnlyTodoTasks} checked={showOnlyTodoTasks} />
                  <KeyboardDoubleArrowDownIcon />
                </Stack>
              </Stack>
              <Box sx={{ textAlign: 'center' }}>
                <TaskList
                  tasks={creditFilteredTasks}
                  onOpenTaskDialog={handleEditTaskDialog}
                  onOpenCommentDialog={handleNewComment}
                  onClickStatusButton={handleStatusChange}
                  onOpenTaskSuppressionDialog={handleTaskDeletionClicked}
                  creditApplication={creditApplication}
                  userCanEditTask={user?.rights.canManageTasks ?? false}
                  editDisabled={editDisabled}
                  isUpdatingTask={isUpdatingTask}
                />
              </Box>
            </Paper>

            {/** Credit section */}
            <CreditSection
              creditApplication={creditApplication}
              onDisplayHistory={() => setOpenDialog(Dialog.CreditDecisionHistory)}
              handleEditBankruptciesClicked={handleEditBankruptciesClicked}
              editDisabled={editDisabled}
              user={user}
            />

            {/** Vouched section */}
            {(() => {
              const isB2cVouched = financingProgramConfig?.useVouched === true
              const isPersonalThinFile = !isB2cVouched && featureFlags.enableVouchedForThinCreditB2B
              const showApplicant = isB2cVouched
                || (isPersonalThinFile && creditApplication.applicant.creditDecision?.warningCodes?.includes('ThinFileWarning'))
              const showCoApplicant = (isB2cVouched && !!creditApplication.coApplicant)
                || (isPersonalThinFile && creditApplication.coApplicant?.creditDecision?.warningCodes?.includes('ThinFileWarning'))
              if (!showApplicant && !showCoApplicant) return null

              const sideBySide = showApplicant && showCoApplicant
              const lgSize = sideBySide ? 6 : 12
              return (
                <Grid container justifyContent="space-between" spacing={2}>
                  {showApplicant && (
                    <Grid size={{ xs: 12, md: 12, lg: lgSize }}>
                      <VouchedSection creditApplication={creditApplication} applicantType={EApplicantType.Applicant} user={user} editDisabled={editDisabled} requireDraftOnly={isB2cVouched} />
                    </Grid>
                  )}
                  {showCoApplicant && (
                    <Grid size={{ xs: 12, md: 12, lg: lgSize }}>
                      <VouchedSection creditApplication={creditApplication} applicantType={EApplicantType.CoApplicant} user={user} editDisabled={editDisabled} requireDraftOnly={isB2cVouched} />
                    </Grid>
                  )}
                </Grid>
              )
            })()}

            {/** Income section */}
            {!financingProgramConfig?.noIncomeValidation && (
              <Paper elevation={3} sx={{ borderRadius: 2, p: 2, mb: 3 }}>
                <Grid container direction="row" rowSpacing={2} spacing={2} marginBottom={1}>
                  <Grid>
                    <Typography variant="h5" component="h5" color="primary">
                      {t('editCreditApplication.income.label')} :
                    </Typography>
                  </Grid>
                  <Grid>
                    {incomeConfirmed ? (
                      <Stack direction="row">
                        <CheckCircle color="success" fontSize="large" style={{ paddingRight: '5%' }} />
                        <Typography marginTop={0.3} variant="h5" component="h5" style={{ color: green[500] }}>
                          {t('editCreditApplication.income.incomeConfirmed')}
                        </Typography>
                      </Stack>
                    ) : (
                      <Tooltip
                        title={hasPendingFraudAlert ? t('editCreditApplication.income.confirmIncomesBlockedByFraudAlert') : ''}
                      >
                        <span>
                          <Button
                            variant="outlined"
                            disabled={
                              !canConfirmIncome || !user?.rights.canManageTasks || applicationIsDraft || editDisabled
                            }
                            onClick={handleConfirmAllIncomes}
                          >
                            {t('editCreditApplication.income.confirmIncomes')}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Grid>
                </Grid>

                <Stack
                  direction={{ md: 'column', lg: 'row' }}
                  spacing={2}
                  divider={<Divider orientation="vertical" flexItem />}
                >
                  <Grid width="100%">
                    <IncomeSection
                      applicant={creditApplication.applicant}
                      onOpenJobUpdateDialog={handleJobUpdateClicked}
                      onOpenCalculatorDialog={handleOpenCalculatorDialog}
                      applicantType={EApplicantType.Applicant}
                      jobTasks={incomeTasks}
                      otherIncomeTasks={otherIncomeTasks}
                      creditApplicationId={creditApplication.id}
                      editDisabled={editDisabled}
                      handleSaveBankAccountRequest={handleSaveBankAccountRequest}
                      requiredExternalStep={findRequiredStepsByApplicantTypeAndExternalStepId(
                        EApplicantType.Applicant,
                        EExternalStep.BankAccount,
                      )}
                      user={user}
                      creditApplication={creditApplication}
                      handleResetBankAccountRequest={handleResetdBankAccountRequest}
                      worksheetIsApproved={worksheetIsApproved}
                    />
                  </Grid>
                  <Grid width="100%">
                    {creditApplication.coApplicant !== null && (
                      <IncomeSection
                        applicant={creditApplication.coApplicant}
                        onOpenJobUpdateDialog={handleJobUpdateClicked}
                        onOpenCalculatorDialog={handleOpenCalculatorDialog}
                        applicantType={EApplicantType.CoApplicant}
                        jobTasks={incomeTasks}
                        otherIncomeTasks={otherIncomeTasks}
                        creditApplicationId={creditApplication.id}
                        editDisabled={editDisabled}
                        handleSaveBankAccountRequest={handleSaveBankAccountRequest}
                        requiredExternalStep={findRequiredStepsByApplicantTypeAndExternalStepId(
                          EApplicantType.CoApplicant,
                          EExternalStep.BankAccount,
                        )}
                        user={user}
                        creditApplication={creditApplication}
                        handleResetBankAccountRequest={handleResetdBankAccountRequest}
                        worksheetIsApproved={worksheetIsApproved}
                      />
                    )}
                  </Grid>
                </Stack>
              </Paper>
            )}

            {/** Bank information by flinks */}
            {financingProgramConfig?.noIncomeValidation && financingProgramConfig?.useFlinksForBankAccountInfo && (
              <Paper elevation={3} sx={{ borderRadius: 2, p: 2, mb: 3 }}>
                <Stack
                  direction={{ md: 'column', lg: 'row' }}
                  spacing={2}
                  divider={<Divider orientation="vertical" flexItem />}
                >
                  <Grid width="100%">
                    <BankInformationSection
                      applicantType={EApplicantType.Applicant}
                      creditApplication={creditApplication}
                      handleSaveBankAccountRequest={handleSaveBankAccountRequest}
                      handleResetBankAccountRequest={handleResetdBankAccountRequest}
                      findRequiredStepsByApplicantTypeAndExternalStepId={
                        findRequiredStepsByApplicantTypeAndExternalStepId
                      }
                      user={user}
                      editDisabled={editDisabled}
                      worksheetIsApproved={worksheetIsApproved}
                    />
                  </Grid>
                  <Grid width="100%">
                    {creditApplication.coApplicant !== null && financingProgramConfig?.supportsCoApplicant && (
                      <BankInformationSection
                        applicantType={EApplicantType.CoApplicant}
                        creditApplication={creditApplication}
                        handleSaveBankAccountRequest={handleSaveBankAccountRequest}
                        handleResetBankAccountRequest={handleResetdBankAccountRequest}
                        findRequiredStepsByApplicantTypeAndExternalStepId={
                          findRequiredStepsByApplicantTypeAndExternalStepId
                        }
                        user={user}
                        editDisabled={editDisabled}
                        worksheetIsApproved={worksheetIsApproved}
                      />
                    )}
                  </Grid>
                </Stack>
              </Paper>
            )}

            {/** worksheet section */}
            {creditApplication.financingProgramId === EFinancingProgram.Auto && (
              <AutoWorksheetInformationSection
                creditApplication={creditApplication}
                worksheet={autoWorksheet!}
                onWorksheetHistoryClicked={() => setOpenDialog(Dialog.WorksheetHistory)}
                user={user}
                editDisabled={editDisabled}
              />
            )}

            {creditApplication.financingProgramId === EFinancingProgram.Personal && (
              <PersonalWorksheetInformationSection
                creditApplication={creditApplication}
                worksheet={personalLoanWorksheet!}
                onWorksheetHistoryClicked={() => setOpenDialog(Dialog.WorksheetHistory)}
                user={user}
                defaultPaymentMethod={merchant.defaultPaymentMethod ?? EPaymentMethod.Check}
                forcePaymentToBorrower={merchant.forcePaymentToBorrower}
                editDisabled={editDisabled}
                continueFinancingDisableReasonList={continueFinancingDisableReasonList}
                handleRefinanceCreditApplication={handleRefinanceCreditApplication}
              />
            )}

            {creditApplication.financingProgramId === EFinancingProgram.B2c && (
              <B2cWorksheetInformationSection
                creditApplication={creditApplication}
                worksheet={b2cWorksheet!}
                user={user}
                editDisabled={editDisabled}
                onWorksheetHistoryClicked={() => setOpenDialog(Dialog.WorksheetHistory)}
              />
            )}

            {creditApplication.financingProgramId === EFinancingProgram.AutoRsla && (
              <AutoRslaWorksheetInformationSection
                worksheet={autoRslaWorksheet!}
                creditApplication={creditApplication}
                user={user}
                editDisabled={editDisabled}
              />
            )}

            {creditApplication.financingProgramId === EFinancingProgram.CreditRsla && (
              <CreditRslaWorksheetInformationSection
                worksheet={creditRslaWorksheet!}
                creditApplication={creditApplication}
                user={user}
                editDisabled={editDisabled}
              />
            )}

            {creditApplication.financingProgramId === EFinancingProgram.Products && (
              <ProductsWorksheetInformationSection
                worksheet={productsWorksheet ?? null}
                creditApplication={creditApplication}
                defaultPaymentMethod={merchant.defaultPaymentMethod ?? EPaymentMethod.Check}
                editDisabled={editDisabled}
                onWorksheetHistoryClicked={() => setOpenDialog(Dialog.WorksheetHistory)}
              />
            )}

            {creditApplication.financingProgramId === EFinancingProgram.IfXpress && (
              <IfXpressWorksheetInformationSection
                creditApplication={creditApplication}
                worksheet={ifXpressWorksheet!}
                user={user}
                editDisabled={editDisabled}
                continueFinancingDisableReasonList={continueFinancingDisableReasonList}
                handleRefinanceCreditApplication={handleRefinanceCreditApplication}
                onWorksheetHistoryClicked={() => setOpenDialog(Dialog.WorksheetHistory)}
              />
            )}

            {creditApplication.financingProgramId === EFinancingProgram.MechanicalWarranty && (
              <MechanicalWarrantyWorksheetInformationSection
                worksheet={mechanicalWarrantyWorksheet ?? null}
                creditApplication={creditApplication}
                editDisabled={editDisabled}
                onWorksheetHistoryClicked={() => setOpenDialog(Dialog.WorksheetHistory)}
              />
            )}

            {creditApplication.financingProgramId === EFinancingProgram.EasyCareProtectionPlan && (
              <EasyCareWorksheetInformationSection
                worksheet={easyCareWorksheet ?? null}
                creditApplication={creditApplication}
                editDisabled={editDisabled}
                onWorksheetHistoryClicked={() => setOpenDialog(Dialog.WorksheetHistory)}
              />
            )}

            {/** Finance contract section */}
            <FinancementContractSection
              handleExpenseClicked={handleExpenseClicked}
              onClickGenerateCvt={handleGenerateCvtNumber}
              onClickPrintCvt={handlePrintCvt}
              onClickResendElectronicSign={handleResendElectronicSign}
              onClickSendElectronicSign={handleSendElectronicSign}
              onClickBankAccount={handleBankAccountClicked}
              disableResendLinkButton={disableResendLinkButton}
              creditApplication={creditApplication}
              worksheet={
                autoWorksheet ??
                personalLoanWorksheet ??
                b2cWorksheet ??
                productsWorksheet ??
                ifXpressWorksheet ??
                easyCareWorksheet ??
                null
              }
              electronicSignatureStatus={electronicSignatureStatus}
              user={user}
              editDisabled={editDisabled}
            />

            {/** Document section */}
            <Paper elevation={3} sx={{ borderRadius: 2, p: 2, mb: 3 }}>
              <DocumentList
                creditApplicationId={id ?? ''}
                financingProgramId={creditApplication.financingProgramId}
                editDisabled={editDisabled}
                user={user}
                worksheet={personalLoanWorksheet ?? null}
              />
            </Paper>

            {/* Dialog */}
            {renderDialog(openDialog, creditApplication)}
          </div>
        </>
      )}
    </>
  )
}

export default ViewCreditApplicationPage
